import React, { useState } from "react";
import { FiUser, FiUserPlus } from "react-icons/fi";
import { Chat, Message } from "../../../types/conversations";
import { BackButton } from "./BackButton";
import { Avatar } from "./Avatar";
import { UserInfo } from "./UserInfo";
import { Menu } from "./Menu";
import { PermissionModal } from "./PermissionModal";
import { useExportConversation } from "./useExportConversation";

export interface ChatHeaderProps {
  chat: Chat;
  onStudentClick?: () => void;
  messages?: Message[];
  currentUserId?: number;
  onBack?: () => void;
  isStudent?: boolean;
  onLinkSuccess?: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  chat,
  onStudentClick,
  messages = [],
  currentUserId,
  onBack,
  isStudent = false,
  onLinkSuccess,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const { handleExportConversation, isExporting, exportSuccess } =
    useExportConversation({ chat, messages, currentUserId });

  const closeMenu = () => setIsMenuOpen(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleViewProfile = () => {
    closeMenu();
    onStudentClick?.();
  };

  const handleExport = async () => {
    closeMenu();
    await handleExportConversation();
  };

  const handleHeaderClick = () => {
    if (isStudent) {
      onStudentClick?.();
    } else {
      setShowPermissionModal(true);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 px-3 py-3 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700 shadow-sm md:px-6 md:py-4 md:gap-4 flex-shrink-0 z-10">
        {onBack && <BackButton onBack={onBack} />}
        <Avatar chat={chat} />
        <UserInfo
          chat={chat}
          isStudent={isStudent}
          onClick={handleHeaderClick}
        />
        {isStudent && (
          <button
            type="button"
            onClick={handleViewProfile}
            className="flex-shrink-0 p-2.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 active:scale-95 touch-manipulation"
            aria-label="Ver perfil"
          >
            <FiUser size={20} className="text-gray-600 dark:text-gray-300" />
          </button>
        )}
        {!isStudent && (
          <button
            type="button"
            onClick={() => setShowPermissionModal(true)}
            className="flex-shrink-0 p-2.5 rounded-full hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-all duration-200 active:scale-95 touch-manipulation"
            aria-label="Tornar meu aluno"
          >
            <FiUserPlus
              size={20}
              className="text-purple-600 dark:text-purple-400"
            />
          </button>
        )}
        <Menu
          isOpen={isMenuOpen}
          onClose={closeMenu}
          onToggle={toggleMenu}
          isStudent={isStudent}
          onViewProfile={handleViewProfile}
          onExportConversation={handleExport}
          isExporting={isExporting}
          exportSuccess={exportSuccess}
        />
      </div>

      <PermissionModal
        isOpen={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        chat={chat}
        onLinkSuccess={onLinkSuccess}
      />
    </>
  );
};
